import { useState, useRef, useEffect } from "react";
import * as pdfjsLib from "pdfjs-dist";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.entry";
import { X } from "lucide-react";
import CfLoader from "../CfLoader/CfLoader";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

export default function PrintRequestModal({
  isOpen,
  onClose,
  totalRecipients,
  pdfUrl,
  isLoading,
  onCreatePrintRequest,
}) {
  const pdfCanvasRef = useRef(null);
  const modalRef = useRef(null);
  const [scale] = useState(0.5);

  const renderPdf = async () => {
    if (!pdfUrl || !pdfCanvasRef.current) return;

    try {
      const loadingTask = pdfjsLib.getDocument({
        url: pdfUrl,
        withCredentials: false,
      });
      const pdf = await loadingTask.promise;
      const page = await pdf.getPage(1);
      const viewport = page.getViewport({ scale });

      pdfCanvasRef.current.height = viewport.height;
      pdfCanvasRef.current.width = viewport.width;

      await page.render({
        canvasContext: pdfCanvasRef.current.getContext("2d"),
        viewport: viewport,
      }).promise;
    } catch (error) {
      console.error("Error rendering PDF:", error);
    }
  };

  useEffect(() => {
    if (isOpen && pdfUrl) {
      renderPdf();
    }
  }, [isOpen, pdfUrl, scale]);

  // Close on outside click
  const handleOverlayClick = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4"
      onClick={handleOverlayClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") handleOverlayClick(e);
      }}
    >
      <div
        ref={modalRef}
        className="bg-cfSecondary rounded-2xl max-w-4xl w-full"
        onClick={(e) => e.stopPropagation()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") e.stopPropagation();
        }}
      >
        <div className="p-4 border-b flex justify-between items-center">
          <div>
            <h3 className="text-lg font-semibold">New Print Request</h3>
            <p className="text-sm text-cfRootBgColor">
              Create a new print request for certificates
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-6">
          <div className="grid grid-cols-2 gap-6">
            {/* Second Document Print Preview */}
            <div className="border rounded-lg p-4">
              <h3 className="text-sm font-medium mb-3">Document Preview</h3>
              <div className="aspect-[1.414] bg-cfCardHover rounded-md overflow-hidden flex items-center justify-center relative">
                {isLoading ? (
                  <CfLoader size="small" />
                ) : pdfUrl ? (
                  <>
                    <canvas
                      ref={pdfCanvasRef}
                      className="max-h-full max-w-full"
                    />
                  </>
                ) : (
                  <div className="text-gray-400">No preview available</div>
                )}
              </div>
            </div>

            {/* Total Recipients */}
            <div className="border rounded-lg p-4 flex flex-col justify-center h-full">
              <div className="h-[10%]">
                <h3 className="text-sm font-medium">Total Recipients</h3>
              </div>
              <div className="h-[90%] items-center w-full flex flex-col justify-center">
                <div className="flex items-center justify-center">
                  <div className="text-5xl font-bold text-center">
                    {totalRecipients}
                  </div>
                </div>
                <p className="text-sm text-cfRootBgColor text-center mt-4">
                  Recipients with available certificates: {totalRecipients}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 border-t flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-cfHeadingText rounded-[8px] shadow-sm text-sm font-medium text-cfCardTagText bg-cfSecondary hover:bg-cfCardHover"
          >
            Cancel
          </button>
          <button
            onClick={onCreatePrintRequest}
            disabled={isLoading}
            className="px-4 py-2 border border-transparent rounded-[8px] shadow-sm text-sm font-medium text-cfSecondary bg-cfPrimaryButton hover:bg-indigo-700 disabled:opacity-50"
          >
            {isLoading ? "Processing..." : "Create Print Request"}
          </button>
        </div>
      </div>
    </div>
  );
}
